import { z } from "zod";
import { phoneIN, uuid, gstin, indianState, isoDate } from "../_common.js";
import { STEP_UP_ACTIONS } from "../step-up-actions.js";

/**
 * `identity.me` — current-session resolver consumed by the SPA's route guards
 * and by any UI that needs the signed-in user's name/role chip.
 *
 * `null` means "no valid session cookie". A non-null result with `company:
 * null` means the user has authenticated (OTP verified) but has not yet
 * completed `createWorkspace` — the SPA should land them on the onboarding
 * funnel instead of the workspace shell.
 */
export const meInput = z.object({});

export const meOutput = z
  .object({
    user: z.object({
      id: uuid,
      name: z.string(),
      phone: phoneIN,
      email: z.string().email().nullable(),
      /**
       * Public read URL for the user's avatar — `null` when the user has
       * never uploaded one OR when S3 isn't configured for this env (the
       * resolver falls back to initials). The URL embeds a cache-busting
       * key segment so reads cache forever; a new upload changes the URL.
       */
      avatarUrl: z.string().url().nullable(),
      /**
       * Whether the user has finished the post-OTP first-fill (real
       * `name` set + `email` populated). When `false`, route guards
       * bounce the user to `/onboarding/account` before letting them
       * reach the workspace or accept an invite. Computed server-side
       * via `isProfileComplete(user)` so the SPA never has to know the
       * placeholder rule (`name === phone`).
       */
      profileComplete: z.boolean(),
      /**
       * ISO timestamps used by the Profile page's Activity card. Carried in
       * `me` (rather than a separate query) because they're tiny and the
       * SPA already cache-locks `me` for 60s — saves a round-trip on
       * Profile open without complicating the cache story.
       */
      /** ISO timestamp when the user verified their email via OTP. Null
       *  when unverified or when the email itself changed since the last
       *  verification. */
      emailVerifiedAt: z.string().nullable(),
      createdAt: z.string(),
      lastSeenAt: z.string().nullable(),
      /**
       * The user's *previous* sign-in — the `issued_at` of the most recent
       * session other than the current one. Distinct from `lastSeenAt`, which
       * `verifyOtp` touches to the *current* sign-in (so it always reads "just
       * now" right after login). Null on the first-ever sign-in (no prior
       * session). Powers the Activity card's "Last sign-in" row.
       */
      previousSignInAt: z.string().nullable(),
    }),
    company: z
      .object({
        id: uuid,
        legalName: z.string(),
        tradeName: z.string().nullable(),
        state: z.string().nullable(),
        role: z.enum(["Founder", "Operator", "Employee"]),
        /**
         * Entitled module strings (`company_entitlements.modules`) — drives
         * web nav-gating so garment groups appear only when the tier unlocks
         * them. Empty for a generic workspace. Hydrated from the same
         * principal the `entitledProcedureFor` gate reads (ADR-0023 §4).
         */
        modules: z.array(z.string()),
        /**
         * Granted permission strings for this member (ADR-0030). Derived from
         * the member's role: system/custom roles carry an explicit list;
         * legacy Founder/Operator fall back via `legacyRolePermissions`.
         * Owner carries `["*"]` (wildcard). Used by the web sidebar and any
         * UI affordance that should hide actions the user can't perform.
         * Enforcement is always server-side; this is display-only.
         */
        permissions: z.array(z.string()),
        /**
         * Master Flint AI switch (migration 0105). Drives client-side gating of
         * the Ask Flint nav entry + the `/ask` route. Enforcement is always
         * server-side (the agent entry points re-check); this is display-only.
         */
        aiEnabled: z.boolean(),
        /**
         * Active subscription summary (ADR-0023 §4). Null when the company
         * has no subscription row yet (rare — only un-seeded test companies).
         * Used by the trial banner and billing settings page; write-mode
         * enforcement is always server-side via the write gate.
         */
        subscription: z
          .object({
            plan: z.string(),
            status: z.string(),
            trialEndsOn: z.string().nullable(),
            /** Days left in trial — non-null only when status is `trialing`. */
            trialDaysLeft: z.number().int().nullable(),
            /** Whether this workspace can write data right now. */
            writeEnabled: z.boolean(),
            /** WhatsApp credits included in the plan per billing cycle. */
            waCreditsBundled: z.number().int(),
            /** WhatsApp credits remaining in the current cycle. */
            waCreditsBalance: z.number().int(),
          })
          .nullable(),
      })
      .nullable(),
  })
  .nullable();

/** `identity.signOut` — revokes the active session and clears the cookie. */
export const signOutInput = z.object({});
export const signOutOutput = z.object({ success: z.literal(true) });

/**
 * `identity.updateProfile` — patch the signed-in user's editable fields.
 *
 * V1.0.x scope: `name` + `email` only. Phone change requires step-up
 * (deferred). Avatar upload uses its own request/confirm pair so the
 * binary path doesn't muddy this transactional schema.
 *
 * Both fields are optional, but at least one must be present (the
 * `.refine` enforces). `email` accepts the empty string as "clear to
 * null" so the form's "delete this value" gesture round-trips. `name`
 * is trimmed to match what the domain stores.
 */
export const updateProfileInput = z
  .object({
    name: z.string().trim().min(1, "Name is required.").max(120, "Name is too long.").optional(),
    email: z
      .union([z.string().email(), z.literal("")])
      .nullable()
      .optional(),
  })
  .refine((v) => v.name !== undefined || v.email !== undefined, {
    message: "Provide at least one field to update.",
  });

export const updateProfileOutput = z.object({
  userId: uuid,
  /**
   * The fields that actually changed. Empty arrays cannot occur — the
   * domain throws `UserNoChangesError` (mapped to 400) before we get
   * here. The SPA uses this to target an inline confirmation toast.
   */
  changedKeys: z.array(z.enum(["name", "email"])).min(1),
});

/**
 * `identity.requestAvatarUpload` — phase 1 of the two-phase avatar upload.
 *
 * Issues a presigned PUT URL the SPA uses to upload directly to S3.
 * Server-side enforcement: contentType is pinned (jpeg/png/webp), size
 * is bounded; the response includes the object key the SPA passes back to
 * `confirmAvatar`. Returns 503 when S3 isn't configured.
 *
 * The PUT itself is NOT a tRPC call — the SPA fetches the URL directly,
 * which is why we expose the raw URL in the wire payload.
 */
export const requestAvatarUploadInput = z.object({
  /** MIME the SPA will send. The signed URL pins this — mismatched PUTs fail. */
  contentType: z.enum(["image/jpeg", "image/png", "image/webp"]),
  /** Bytes the client intends to upload — capped at 2 MiB server-side. */
  size: z
    .number()
    .int()
    .positive()
    .max(2 * 1024 * 1024),
});

export const requestAvatarUploadOutput = z.object({
  /** Presigned PUT URL — short-lived (5 min). */
  uploadUrl: z.string().url(),
  /** S3 object key the SPA passes to `confirmAvatar` after PUT succeeds. */
  objectKey: z.string(),
  /** Echoed for the SPA's pre-upload size check. */
  maxBytes: z.number().int().positive(),
  /** Echoed so the SPA pins the same Content-Type header on the PUT. */
  contentType: z.enum(["image/jpeg", "image/png", "image/webp"]),
});

/**
 * `identity.confirmAvatar` — phase 2. SPA calls this after the PUT finishes
 * to flip the user row's `avatar_object_key`. Returns the new public URL
 * (which may be `null` if no public host is configured) and the SPA
 * invalidates `identity.me`.
 */
export const confirmAvatarInput = z.object({
  objectKey: z.string().min(1).max(512),
});

export const confirmAvatarOutput = z.object({
  userId: uuid,
  objectKey: z.string(),
  avatarUrl: z.string().url().nullable(),
});

/**
 * `identity.completeProfile` — first-fill of `name` + `email` after a
 * fresh OTP verification. Both fields are mandatory (this is the gate;
 * `updateProfile` is the post-onboarding edit path).
 *
 * Stricter than `updateProfileInput`:
 *   - `name` must be present, min 2.
 *   - `email` must be present, must validate.
 *   - `name === user.phone` is rejected by the domain (returned as
 *     UNPROCESSABLE) so the obvious bypass doesn't slip through.
 *
 * Idempotent — calling with values that already match the row succeeds
 * with `changed: false`. The SPA can call it on every guard re-run
 * without painting a "Profile updated" toast each time.
 */
export const completeProfileInput = z.object({
  name: z.string().trim().min(2, "Name is required.").max(120, "Name is too long."),
  email: z.string().trim().email("Enter a valid email address.").max(255),
});

export const completeProfileOutput = z.object({
  userId: uuid,
  name: z.string(),
  email: z.string().email(),
  changed: z.boolean(),
});

export const requestOtpInput = z.object({
  phone: phoneIN,
  /** WhatsApp is secondary channel for re-auth on familiar devices (system-decisions §1). */
  channel: z.enum(["sms", "whatsapp"]).default("sms"),
});

export const requestOtpOutput = z.object({
  challengeId: uuid,
  expiresInSeconds: z.number().int().positive(),
});

export const verifyOtpInput = z.object({
  challengeId: uuid,
  code: z.string().regex(/^\d{6}$/, "OTP is 6 digits"),
});

export const verifyOtpOutput = z.object({
  /** What the system did with this verified phone. */
  outcome: z.enum(["existing-membership", "invite-pending", "no-membership"]),
  userId: uuid,
});

// ── Step-up OTP ──────────────────────────────────────────────────
//
// Pair of procedures that gate a sensitive operation behind a fresh OTP.
// `action` is one of `STEP_UP_ACTIONS` (see `step-up-actions.ts`); both
// procedures + the matching server middleware reference the same enum
// so a typo on either side is a compile error.
//
// Flow: SPA opens dialog → `requestStepUpOtp` → SMS/WhatsApp sent → user
// types 6 digits → `verifyStepUpOtp` → server stamps session evidence →
// SPA invokes the gated mutation while evidence is fresh (5-minute TTL).

const stepUpActionEnum = z.enum(Object.values(STEP_UP_ACTIONS) as [string, ...string[]]);

export const requestStepUpOtpInput = z.object({
  /** Channel preference — SMS or WhatsApp. Same options as sign-in. */
  channel: z.enum(["sms", "whatsapp"]).default("sms"),
  /** Which sensitive operation the OTP authorises. */
  action: stepUpActionEnum,
});

export const requestStepUpOtpOutput = z.object({
  challengeId: uuid,
  expiresInSeconds: z.number().int().positive(),
});

export const verifyStepUpOtpInput = z.object({
  challengeId: uuid,
  code: z.string().regex(/^\d{6}$/, "OTP is 6 digits"),
  /** Echoed action — must match the one passed to `requestStepUpOtp`. */
  action: stepUpActionEnum,
});

export const verifyStepUpOtpOutput = z.object({
  /** TTL in seconds for the freshly-stamped evidence. */
  validForSeconds: z.number().int().positive(),
  /** Echo of the verified action — SPA uses it to gate the follow-up call. */
  action: stepUpActionEnum,
});

// ── Email verify OTP ─────────────────────────────────────────────
//
// Pair of procedures that verify the user's email address. The server
// reads the email from the session user — the SPA sends no input.
//
// Flow: SPA opens dialog → `requestEmailVerifyOtp` → email sent → user
// types 6 digits → `verifyEmailOtp` → server stamps `email_verified_at`
// → SPA invalidates `me` so the pill flips.

/** Input is empty — the server reads the email from the session user. */
export const requestEmailVerifyOtpInput = z.object({});

export const requestEmailVerifyOtpOutput = z.object({
  challengeId: uuid,
  expiresInSeconds: z.number().int().positive(),
});

export const verifyEmailOtpInput = z.object({
  challengeId: uuid,
  code: z.string().regex(/^\d{6}$/, "OTP is 6 digits"),
});

export const verifyEmailOtpOutput = z.object({
  /** ISO string of the newly-stamped `email_verified_at`. */
  emailVerifiedAt: z.string(),
});

export const createWorkspaceInput = z.object({
  founderName: z.string().min(1, "Founder name is required.").max(120, "Founder name is too long."),
  legalName: z.string().min(1, "Company name is required.").max(255, "Company name is too long."),
  /** Trade name — defaults to legalName if omitted. Prototype S03 captures both. */
  tradeName: z.string().max(255).optional(),
  workEmail: z.string().email(),
  /** Drives intra-state vs inter-state GST default at first invoice (compliance/gst-calc). */
  state: indianState,
  gstin: gstin.optional(),
});

export const createWorkspaceOutput = z.object({
  companyId: uuid,
  membershipId: uuid,
  /** Used by S05 for the trial-days-left chip; mirrors subscriptions.trial_ends_on. */
  trialEndsOn: isoDate,
});
